/**
 * Subscription-plan resolution. Ported from quote-gen `plans.py`.
 *
 * Reads `productPlans` — the same collection open-api's `planFeatures` resolver reads —
 * so a quoted subscription line matches what the portal would charge:
 *   `plans[currency][term].price` = per-screen amount in dollars.
 *   For `yearly` that price is the ANNUAL per-screen figure (usd standard = 108/yr).
 *
 * As on the hardware side, substring matching is intentionally absent: it silently
 * mis-priced "pro" onto Pro Plus. We never guess a subscription price.
 */
import { toCents } from './cents';
import type { CatalogPlan, PlanTerm, PlanTier } from './types';
import type { MatchResult } from './catalog';

/**
 * Enterprise is not a `productPlans` document, so this PDF-only tool carries it as a fixed
 * figure: USD $486/screen/yr (the annual 10% inside), 25 screens or more, USD only
 * (requester, 2026-11-03). Prod's Stripe Enterprise price (`price_1OtxGp…`) carries USD only
 * too; if that price changes, change this number with it.
 */
export const ENTERPRISE_ALIAS = 'enterprise';
export const ENTERPRISE_MIN_SCREENS = 25;
export const ENTERPRISE_PLAN: CatalogPlan = {
  _id: ENTERPRISE_ALIAS,
  alias: ENTERPRISE_ALIAS,
  name: 'Enterprise',
  plans: { usd: { yearly: { price: 486 } } },
};

/** Quotes are annual-only, so this is the term every line defaults to. */
export const DEFAULT_TERM: PlanTerm = 'yearly';

function norm(s: string | undefined | null): string {
  return (s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function collapse(s: string | undefined | null): string {
  return (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * Free-text term → the DB's canonical key.
 *
 * `productPlans` keys yearly prices under `yearly` ONLY and the lookup is an exact dict
 * hit, so an un-normalized "annual" silently misses and reports "no price for term
 * 'annual'". The only special key preserved is `yearly_freedevice`.
 *
 * Everything else — including anything month-ish — resolves to `yearly`. Quotes are
 * annual, so there is no term for this to get wrong: a caller asking for monthly gets an
 * annual price, not a dropped line.
 */
export function normalizeTerm(term: string | undefined | null): PlanTerm {
  const t = (term || DEFAULT_TERM).toLowerCase().trim();
  if (t.includes('freedevice') || t.includes('free_device') || t.includes('free device')) {
    return 'yearly_freedevice';
  }
  return 'yearly';
}

export interface ResolvedPlanPrice {
  cents: number | null;
  fallback: boolean;
  term: PlanTerm;
  stripePlanId: string | null;
  display: string | null;
}

/**
 * Per-screen price in cents for a plan at a currency + term. Falls back to USD when the
 * requested currency isn't present (flagged — a USD figure on a GBP quote is wrong, not
 * merely imprecise).
 */
export function resolvePlanPrice(
  doc: CatalogPlan,
  currency: string,
  term?: string,
): ResolvedPlanPrice {
  const cur = (currency || 'usd').toLowerCase();
  const normalized = normalizeTerm(term);
  const byCurrency = doc.plans || {};

  let node = byCurrency[cur];
  let fallback = false;
  if (!node) {
    node = byCurrency.usd;
    fallback = true;
  }

  const tier: PlanTier | undefined = node?.[normalized];
  if (!tier || tier.price === undefined || tier.price === null) {
    return { cents: null, fallback, term: normalized, stripePlanId: null, display: null };
  }

  return {
    cents: toCents(tier.price),
    fallback,
    term: normalized,
    stripePlanId: tier.stripe_plan_id ?? null,
    display: tier.plan ?? null,
  };
}

/** Exact alias / `_id` / normalized-or-collapsed name. No substring matching, by design. */
export function matchPlan(
  query: string | undefined,
  alias: string | undefined,
  plans: CatalogPlan[],
): MatchResult<CatalogPlan> {
  for (const ident of [alias, query]) {
    if (!ident) continue;
    const a = ident.toLowerCase().trim();
    const ac = collapse(ident);
    for (const p of plans) {
      if (a === (p.alias || '').toLowerCase() || a === (p._id || '').toLowerCase()) {
        return { match: p, reason: 'alias' };
      }
      if (ac && (ac === collapse(p.alias) || ac === collapse(p._id) || ac === collapse(p.name))) {
        return { match: p, reason: 'collapsed-exact' };
      }
    }
  }

  const q = norm(query);
  if (q) {
    const exact = plans.filter((p) => q === norm(p.name) || q === norm(p.alias));
    if (exact.length === 1) return { match: exact[0], reason: 'exact-name' };
  }

  const ident = alias || query;
  return {
    match: null,
    reason: ident ? `'${ident}' is not an exact plan match` : 'nothing selected',
  };
}
